import React, { useEffect, useState } from "react";
import {
  Button,
  Box,
  Checkbox,
  TextField,
  Typography,
  Chip,
  InputAdornment,
  FormControlLabel,
} from "@mui/material/";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import axios from "axios";
import { LoadingButton } from "@mui/lab";
import { useDialog } from "../../../DialogContext";
import { backend_api } from "../../../../static/server";
import {
  getActivities,
  updateActivity,
} from "../../../../redux/Apps/Lawyerapp/ActivitySlice/ActivitySlice";
import { pageScrollStyle } from "../../../../styles/styles";
import DrawerTemplate from "../../Cases/DrawerTemplate";
import MatterApiField from "../../Settings/FieldsUi/MatterApiField";
import { getCurrencySymbol } from "../../../../pages/LawyerPages/Settings/CurrencySetForm";
import { isDevMode } from "../../../Auth/SubscriptionContext";
import { formatStopwatchSeconds } from "../../../../utils/stopwatchSession";
import {
  resolveMatterCurrency,
  resolveMatterHourlyRate,
} from "../../../../utils/matterBilling";

const EMPTY_ELAPSED_TIME = "00:00:00";

const getTimeMetrics = (startValue, endValue) => {
  const startTime = dayjs(startValue);
  const endTime = dayjs(endValue);

  if (!startTime.isValid() || !endTime.isValid()) {
    return null;
  }

  const startTimeMs = startTime.valueOf();
  const endTimeMs = endTime.valueOf();

  if (endTimeMs <= startTimeMs) {
    return null;
  }

  const diffInMs = endTimeMs - startTimeMs;
  const diffInSeconds = Math.floor(diffInMs / 1000);
  const diffInHours = diffInMs / (1000 * 60 * 60);

  return {
    diffInHours,
    diffInSeconds,
    formattedElapsedTime: formatStopwatchSeconds(diffInSeconds),
  };
};

const ServiceTimeForm = ({
  entryData,
  editMode = false,
  onSuccess,
  timeIn = null,
  timeOut = null,
  elapsedTime: initialElapsedTime = null,
}) => {
  const { showDialog, closeDialog } = useDialog();
  const dispatch = useDispatch();
  const { token, defaultCurrency } = useSelector((data) => data.user);
  const [totalHours, setTotalHours] = useState(0);
  const [totalSeconds, setTotalSeconds] = useState(0);
  const [totalAmount, setTotalAmount] = useState("0.00");
  const [submitLoading, setSubmitLoading] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(EMPTY_ELAPSED_TIME);
  const [isEditMode] = useState(editMode);
  const [selectedMatterCurrency, setSelectedMatterCurrency] = useState(null);
  const [currencySymbol, setCurrencySymbol] = useState(
    getCurrencySymbol(defaultCurrency),
  );

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      type: "time",
      case: null,
      time_in: null,
      time_out: null,
      hourly_rate: "",
      description: "",
      non_billable: false,
      bill_entry_show: false,
      currency: defaultCurrency || "USD",
    },
  });

  const time_in = watch("time_in");
  const time_out = watch("time_out");
  const hourly_rate = watch("hourly_rate");
  const nonBillable = watch("non_billable");
  const selectedCase = watch("case");
  const formCurrency = watch("currency");

  useEffect(() => {
    const symbol = getCurrencySymbol(formCurrency);
    setCurrencySymbol(symbol);
  }, [formCurrency]);

  const fetchMatterDetails = async (caseId) => {
    if (!caseId) return null;

    try {
      const response = await axios.get(
        `${backend_api}case/register/${caseId}/`,
        {
          headers: {
            Authorization: `Token ${token}`,
          },
        },
      );

      return response.data || null;
    } catch (error) {
      console.error("Error fetching matter details:", error);
      return null;
    }
  };

  const applyMatterDefaults = async (selectedValue) => {
    if (!selectedValue) {
      setValue("case", null);
      setValue("currency", defaultCurrency || "USD");
      setValue("hourly_rate", "");
      setSelectedMatterCurrency(null);
      return;
    }

    const matterId =
      typeof selectedValue === "object" && selectedValue !== null
        ? selectedValue.id
        : selectedValue;

    setValue("case", matterId);

    let matterData =
      typeof selectedValue === "object" && selectedValue !== null
        ? selectedValue
        : null;

    const localCurrency = resolveMatterCurrency(matterData, "");
    const localHourlyRate = resolveMatterHourlyRate(matterData);

    if ((!localCurrency || !localHourlyRate) && matterId) {
      const fetchedMatter = await fetchMatterDetails(matterId);
      if (fetchedMatter) {
        matterData = fetchedMatter;
      }
    }

    const resolvedMatterCurrency = resolveMatterCurrency(matterData, "");
    const resolvedHourlyRate = resolveMatterHourlyRate(matterData);

    if (resolvedMatterCurrency) {
      setValue("currency", resolvedMatterCurrency, { shouldValidate: true });
      setSelectedMatterCurrency(resolvedMatterCurrency);
    } else {
      setValue("currency", defaultCurrency || "USD", { shouldValidate: true });
      setSelectedMatterCurrency(null);
    }

    setValue("hourly_rate", resolvedHourlyRate || "", {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  // Set edit data
  useEffect(() => {
    if (entryData && isEditMode) {

      reset({
        type: "time",
        case: entryData.case?.id || null,
        time_in: entryData.time_in || null,
        time_out: entryData.time_out || null,
        hourly_rate: entryData.hourly_rate || "",
        description: entryData.description || "",
        non_billable: entryData.non_billable || false,
        bill_entry_show: entryData.bill_entry_show || false,
        currency: entryData.currency || defaultCurrency || "USD",
      });

      // If case has currency, set it
      if (entryData.case?.currency) {
        setSelectedMatterCurrency(entryData.case.currency);
      } else if (entryData.currency) {
        setSelectedMatterCurrency(entryData.currency);
      }
    }
  }, [entryData, isEditMode, reset, defaultCurrency]);

  useEffect(() => {
    if (isEditMode || !timeIn || !timeOut) {
      return;
    }

    const normalizedTimeIn = dayjs(timeIn);
    const normalizedTimeOut = dayjs(timeOut);

    if (!normalizedTimeIn.isValid() || !normalizedTimeOut.isValid()) {
      return;
    }

    reset({
      type: "time",
      case: null,
      time_in: normalizedTimeIn.toISOString(),
      time_out: normalizedTimeOut.toISOString(),
      hourly_rate: "",
      description: "",
      non_billable: false,
      bill_entry_show: false,
      currency: defaultCurrency || "USD",
    });
    setSelectedMatterCurrency(null);

    if (initialElapsedTime) {
      setElapsedTime(initialElapsedTime);
    }
  }, [defaultCurrency, initialElapsedTime, isEditMode, reset, timeIn, timeOut]);

  useEffect(() => {
    const metrics = getTimeMetrics(time_in, time_out);

    if (!metrics) {
      setElapsedTime(EMPTY_ELAPSED_TIME);
      setTotalSeconds(0);
      setTotalHours(0);
      setTotalAmount("0.00");
      return;
    }

    setElapsedTime(metrics.formattedElapsedTime);
    setTotalSeconds(metrics.diffInSeconds);
    setTotalHours(metrics.diffInHours);

    if (hourly_rate && !nonBillable) {
      const amount = (
        metrics.diffInHours *
        parseFloat(hourly_rate || 0)
      ).toFixed(2);
      setTotalAmount(amount);
    } else if (nonBillable) {
      setTotalAmount("0.00");
    } else {
      setTotalAmount("0.00");
    }
  }, [time_in, time_out, hourly_rate, nonBillable]);

  const calculateTotalAmount = (seconds, rate) => {
    if (!seconds || !rate) return "0.00";
    const hours = seconds / 3600;
    return (hours * parseFloat(rate)).toFixed(2);
  };

  const onSubmit = async (data) => {
    setSubmitLoading(true);
    try {
      let responseData = null;
      if (isEditMode) {
        responseData = await dispatch(
          updateActivity({
            url: `${backend_api}firm-case/expense/${entryData?.id}/`,
            data: data,
            token: token,
          }),
        ).unwrap();
        toast.success("Work session updated successfully");
      } else {
        const response = await axios.post(
          `${backend_api}firm-case/expense/`,
          data,
          {
            headers: {
              Authorization: `Token ${token}`,
            },
          },
        );
        responseData = response.data;
        toast.success("Work session created successfully");
      }

      if (onSuccess && responseData) onSuccess(responseData);

      reset();
      setSelectedMatterCurrency(null);
      dispatch(
        getActivities({
          url: `${backend_api}firm-case/expense/`,
          token: token,
        }),
      );
    } catch (error) {
      toast.error("Failed to save entry");
    } finally {
      setSubmitLoading(false);
      closeDialog();
    }
  };

  const timeFormat = "MM/DD/YYYY HH:mm:ss";

  const isFormValid =
    !!selectedCase &&
    !!time_in &&
    !!time_out &&
    !!hourly_rate &&
    totalSeconds > 0;

  return (
    <Box sx={{ position: "relative" }}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "1rem",
            p: "1rem",
          }}
        >
          <Box>
            <Controller
              name="case"
              control={control}
              rules={{ required: "Matter is required" }}
              render={({ field }) => (
                <MatterApiField
                  label="Matter"
                  value={field.value}
                  onChange={async (value) => {
                    await applyMatterDefaults(value);
                    if (typeof value === "object" && value !== null) {
                      field.onChange(value.id);
                    } else {
                      field.onChange(value);
                    }
                  }}
                  errors={errors}
                  token={token}
                  apiUrl={`${backend_api}case/register/`}
                  isRequired={true}
                  AddMatterFunc={({ onCreated }) =>
                    showDialog(<DrawerTemplate onMatterCreated={onCreated} />, {
                      dialogTitle: "Select Template",
                    })
                  }
                  disabled={isEditMode}
                  returnFullData={true}
                  editData={entryData?.case || null}
                />
              )}
            />
          </Box>

          {/* Display current currency */}
          {isDevMode && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Currency:
              </Typography>
              <Chip
                label={`${formCurrency} ${currencySymbol}`}
                size="small"
                variant="outlined"
                sx={{ fontWeight: 500 }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ ml: 1 }}
              >
                {selectedMatterCurrency
                  ? `(Using matter's currency)`
                  : `(Using your default currency)`}
              </Typography>
            </Box>
          )}

          <Box sx={{ display: "flex", gap: "1rem" }}>
            <Controller
              name="time_in"
              control={control}
              rules={{ required: "Time in is required" }}
              render={({ field }) => (
                <DateTimePicker
                  value={field.value ? dayjs(field.value) : null}
                  onChange={(newValue) => {
                    const isValidDate = newValue && dayjs(newValue).isValid();
                    field.onChange(
                      isValidDate ? dayjs(newValue).toISOString() : null,
                    );
                  }}
                  slotProps={{
                    textField: {
                      label: (
                        <span>
                          Time In<span style={{ color: "red" }}>*</span>
                        </span>
                      ),
                      size: "small",
                      variant: "outlined",
                      fullWidth: true,
                      error: !!errors?.time_in,
                      helperText: errors?.time_in?.message,
                    },
                    popper: {
                      sx: {
                        zIndex: 1700,
                        "& .MuiPickersPopper-root": {
                          maxWidth: "100%",
                          maxHeight: "100%",
                          overflow: "hidden",
                        },
                        "& .MuiPaper-root": {
                          width: "fit-content",
                          maxHeight: "15rem",
                          overflowY: "auto",
                          ...pageScrollStyle,
                        },
                        "& .MuiList-root": {
                          ...pageScrollStyle,
                        },
                        "& .MuiMenuItem-root": {
                          borderRadius: "0.5rem",
                        },
                      },
                      container: document.body,
                    },
                  }}
                  format={timeFormat}
                  maxDate={dayjs()}
                  views={[
                    "year",
                    "month",
                    "day",
                    "hours",
                    "minutes",
                    "seconds",
                  ]}
                  ampm={false}
                />
              )}
            />
            <Controller
              name="time_out"
              control={control}
              rules={{ required: "Time out is required" }}
              render={({ field }) => (
                <DateTimePicker
                  value={field.value ? dayjs(field.value) : null}
                  onChange={(newValue) => {
                    const isValidDate = newValue && dayjs(newValue).isValid();
                    field.onChange(
                      isValidDate ? dayjs(newValue).toISOString() : null,
                    );
                  }}
                  slotProps={{
                    textField: {
                      label: (
                        <span>
                          Time Out<span style={{ color: "red" }}>*</span>
                        </span>
                      ),
                      size: "small",
                      variant: "outlined",
                      fullWidth: true,
                      error: !!errors?.time_out,
                      helperText: errors?.time_out?.message,
                    },
                    popper: {
                      sx: {
                        zIndex: 1600,
                        "& .MuiPickersPopper-root": {
                          maxWidth: "100%",
                          maxHeight: "100%",
                          overflow: "hidden",
                        },
                        "& .MuiPaper-root": {
                          width: "fit-content",
                          maxHeight: "15rem",
                          overflowY: "auto",
                          ...pageScrollStyle,
                        },
                        "& .MuiList-root": {
                          ...pageScrollStyle,
                        },
                        "& .MuiMenuItem-root": {
                          borderRadius: "0.5rem",
                        },
                      },
                      container: document.body,
                    },
                  }}
                  format={timeFormat}
                  minDate={time_in ? dayjs(time_in).startOf("day") : null}
                  disabled={!time_in}
                  views={[
                    "year",
                    "month",
                    "day",
                    "hours",
                    "minutes",
                    "seconds",
                  ]}
                  ampm={false}
                />
              )}
            />
          </Box>
          <Box sx={{ display: "flex", gap: "1rem" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                border: "1px solid  #c5c5c7",
                borderRadius: "0.2rem",
                px: "0.5rem",
                py: "0.5rem",
                width: "100%",
              }}
            >
              <Typography
                variant="body1"
                sx={{ fontSize: "0.8rem", fontWeight: "500" }}
              >
                Total Time (HH:MM:SS)
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: "0.9rem",
                  fontWeight: "600",
                  fontFamily: "monospace",
                }}
              >
                {elapsedTime || "00:00:00"}
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                border: "1px solid  #c5c5c7",
                borderRadius: "0.2rem",
                px: "0.5rem",
                py: "0.5rem",
                width: "100%",
              }}
            >
              <Typography
                variant="body1"
                sx={{ fontSize: "0.8rem", fontWeight: "500" }}
              >
                Total Hours (Decimal)
              </Typography>
              <Typography
                variant="body1"
                sx={{ fontSize: "0.9rem", fontWeight: "600" }}
              >
                {totalHours.toFixed(4)}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", gap: "1rem" }}>
            <Controller
              name="hourly_rate"
              control={control}
              defaultValue=""
              rules={{ required: "Hourly rate is required" }}
              render={({ field }) => (
                <TextField
                  size="small"
                  {...field}
                  label={
                    <span>
                      Hourly Rate<span style={{ color: "red" }}>*</span>
                    </span>
                  }
                  type="number"
                  fullWidth
                  error={!!errors?.hourly_rate}
                  helperText={errors?.hourly_rate?.message}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Box
                          sx={{
                            px: 1,
                            py: 0.5,
                            borderRadius: "6px",
                            backgroundColor: "white",
                            fontSize: "0.875rem",
                            fontWeight: 500,
                            color: "text.secondary",
                          }}
                        >
                          {currencySymbol}
                        </Box>
                      </InputAdornment>
                    ),
                  }}
                  onChange={(e) => {
                    field.onChange(e);
                    if (!nonBillable && totalSeconds > 0 && e.target.value) {
                      const amount = calculateTotalAmount(
                        totalSeconds,
                        e.target.value,
                      );
                      setTotalAmount(amount);
                    }
                  }}
                />
              )}
            />
            <Box
              sx={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                border: "1px solid #c5c5c7",
                borderRadius: "0.2rem",
                px: "0.5rem",
                py: "0.5rem",
                bgcolor: nonBillable ? "#f5f5f5" : "#e3f2fd",
              }}
            >
              <Typography
                variant="body1"
                sx={{ fontSize: "0.9rem", fontWeight: "500" }}
              >
                Total Amount
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: "1rem",
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <Typography
                  component="span"
                  sx={{
                    fontSize: "0.85rem",
                    color: nonBillable ? "#9e9e9e" : "text.secondary",
                  }}
                >
                  {currencySymbol}
                </Typography>

                <Typography
                  component="span"
                  sx={{
                    fontSize: "1rem",
                    fontWeight: 700,
                    color: nonBillable ? "#9e9e9e" : "primary.main",
                  }}
                >
                  {nonBillable ? "0.00" : totalAmount}
                </Typography>
              </Typography>
            </Box>
          </Box>

          <Controller
            name="description"
            control={control}
            defaultValue=""
            rules={{ required: "Description is required" }}
            render={({ field }) => (
              <TextField
                size="small"
                {...field}
                label={
                  <span>
                    Describe your work
                    <span style={{ color: "red" }}>*</span>
                  </span>
                }
                multiline
                rows={3}
                fullWidth
                error={!!errors?.description}
                helperText={errors?.description?.message}
              />
            )}
          />
          <Box sx={{ display: "flex", gap: "1rem" }}>
            <Controller
              name="non_billable"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      {...field}
                      size="small"
                      checked={field.value}
                      onChange={(e) => {
                        const newValue = e.target.checked;
                        field.onChange(newValue);
                        if (newValue) {
                          setTotalAmount("0.00");
                        } else if (hourly_rate && totalSeconds > 0) {
                          const amount = calculateTotalAmount(
                            totalSeconds,
                            hourly_rate,
                          );
                          setTotalAmount(amount);
                        }
                      }}
                    />
                  }
                  label="Non-Billable"
                  sx={{
                    "& .MuiFormControlLabel-label": {
                      fontSize: "0.8rem",
                      fontWeight: 500,
                      color: "text.secondary",
                    },
                  }}
                />
              )}
            />
            <Controller
              name="bill_entry_show"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      {...field}
                      size="small"
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                      disabled={!nonBillable}
                    />
                  }
                  label="Show Entry on Bill"
                  sx={{
                    "& .MuiFormControlLabel-label": {
                      fontSize: "0.8rem",
                      fontWeight: 500,
                      color: "text.secondary",
                    },
                  }}
                />
              )}
            />
          </Box>
        </Box>
        <Box
          sx={{
            height: "fit-content",
            borderTop: "1px solid #ddd",
            position: "sticky",
            bottom: 0,
            backdropFilter: "blur(10px)",
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            p: "0.5rem",
            zIndex: 1000,
            gap: "0.5rem",
          }}
        >
          <Button size="small" variant="outlined" onClick={() => closeDialog()}>
            Cancel
          </Button>
          <LoadingButton
            loading={submitLoading}
            variant="contained"
            color="primary"
            type="submit"
            size="small"
            disabled={!isFormValid}
          >
            {isEditMode ? "Update Time Entry" : "Enter Time"}
          </LoadingButton>
        </Box>
      </form>
    </Box>
  );
};

export default ServiceTimeForm;
